import useTitanApp from "@/hooks/useTitanApp";
import {
  addPatientCondtionRelated,
  getPatientConditionRelated,
  deletePatientConditionRelated,
} from "@/dispatchers/patientDispatchers";

export interface Condition {
  id: string;
  name: string;
  status: "active" | "resolved" | "chronic";
  diagnosedDate: string;
  notes: string;
}

export const conditionStatusOptions = [
  { value: "active", label: "Active", color: "green" },
  { value: "chronic", label: "Chronic", color: "orange" },
  { value: "resolved", label: "Resolved", color: "gray" },
] as const;

// ✅ map string status → backend number
const mapStatusToNumber = (status: string): number => {
  switch (status) {
    case "active":
      return 1;
    case "resolved":
      return 2;
    case "chronic":
      return 3;
    default:
      return 1;
  }
};

// ✅ map backend number → UI string
const mapStatus = (status: number): "active" | "resolved" | "chronic" => {
  switch (status) {
    case 1:
      return "active";
    case 2:
      return "resolved";
    case 3:
      return "chronic";
    default:
      return "active";
  }
};

export const useMedicalConditionsState = () => {
  const { patientState, appState } = useTitanApp();
  const { darkMode } = appState || {};
  const {
    patientCondtionRelatedLoading,
    patientConditionRelated,
    selectedPatientDetails,
  } = patientState || {};
  const { patientID } = selectedPatientDetails || {};

  // Map backend response → UI
  const DashboardPatientConditions: Condition[] =
    patientConditionRelated?.map((c, index) => ({
      id: String(index + 1), // fallback index
      name: c.conditionName || "",
      status: mapStatus(c.status),
      diagnosedDate: c.diagnosedDate || "",
      notes: c.additionalNotes || "",
    })) || [];

  return {
    patientCondtionRelatedLoading,
    DashboardPatientConditions,
    patientID,
    darkMode,
  };
};

// ✅ Add/save a condition
export const savePatientCondition = async (
  conditionData: any,
  patientId: string
) => {
  if (!patientId) {
    console.error("❌ No patientId provided for savePatientCondition");
    return;
  }

  try {
    const formattedCondition = {
      conditionName: conditionData.conditionName,
      status: mapStatusToNumber(conditionData.status),
      diagnosedDate: conditionData.diagnosedDate || null,
      additionalNotes: conditionData.notes || "",
    };

    const payload = {
      patientId,
      relatedConditions: [formattedCondition], // backend expects array
    };

    await addPatientCondtionRelated(payload);
    await getPatientConditionRelated(patientId);
  } catch (err) {
    console.error("❌ Failed to save condition:", err);
  }
};

// ✅ Update/edit a condition
export const updatePatientCondition = async (
  index: number,
  updatedCondition: any,
  patientId: string,
  existingConditions: any[]
) => {
  if (!patientId) {
    console.error("❌ No patientId provided for updatePatientCondition");
    return;
  }

  try {
    // old one (backend shape, from state)
    const oldCondition = existingConditions[index];
    if (!oldCondition) {
      console.error("❌ No condition found at index:", index);
      return;
    }

    const oldFormatted = {
      conditionName: oldCondition.conditionName ?? oldCondition.name,
      status:
        typeof oldCondition.status === "string"
          ? mapStatusToNumber(oldCondition.status)
          : oldCondition.status,
      diagnosedDate: oldCondition.diagnosedDate || null,
      additionalNotes: oldCondition.additionalNotes ?? oldCondition.notes,
    };

    // new one (backend shape, from form)
    const newFormatted = {
      conditionName: updatedCondition.conditionName,
      status: mapStatusToNumber(updatedCondition.status),
      diagnosedDate: updatedCondition.diagnosedDate || null,
      additionalNotes: updatedCondition.notes || "",
    };

    const payload = {
      patientId,
      relatedConditions: [oldFormatted, newFormatted], // backend expects 2 objects
    };

    await addPatientCondtionRelated(payload);
    await getPatientConditionRelated(patientId);
  } catch (err) {
    console.error("❌ Failed to update condition:", err);
  }
};

// ✅ Delete a condition
export const deletePatientCondition = async (
  conditionToDelete: any,
  patientId: string
) => {
  if (!patientId) {
    console.error("❌ No patientId provided for deletePatientCondition");
    return;
  }

  try {
    const formattedCondition = {
      conditionName: conditionToDelete.conditionName ?? conditionToDelete.name,
      status:
        typeof conditionToDelete.status === "string"
          ? mapStatusToNumber(conditionToDelete.status)
          : conditionToDelete.status,
      diagnosedDate: conditionToDelete.diagnosedDate || null,
      additionalNotes:
        conditionToDelete.additionalNotes ?? conditionToDelete.notes,
    };

    const payload = {
      patientId,
      relatedConditions: [formattedCondition], // backend expects array
    };
    await deletePatientConditionRelated(payload);
    await getPatientConditionRelated(patientId);
  } catch (err) {
    console.error("❌ Failed to delete condition:", err);
  }
};
